import { Bar } from 'react-chartjs-2'
import type { WidgetSettings } from '../../stores/dashboardLayoutStore'
import { useChartColors } from '../../hooks/useChartColors'
import { useThemeStore } from '../../stores/themeStore'

export interface StoreSalesBucket {
    storeId: string;
    storeName: string;
    storeNumber: string;
    sales: number;
    discount: number;
    lastYearSales?: number;
}

export default function SalesByStoreChart({ 
    data, 
    settings,
    selectedStoreId
}: { 
    data: StoreSalesBucket[], 
    settings?: WidgetSettings,
    selectedStoreId?: string
}) {
    const colors = useChartColors()
    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? '#94a3b8' : '#475569'
    const gridColor = colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
    
    // We get the selected store from the dashboard page via props
    // Could be 'all', 'group', or a specific LegacyStoreId.
    // If it's a specific string, we want to highlight it. If it's all or group, we might not highlight anything specifically unless specified differently.

    // Sort data and filter out hidden/zero-value stores
    const sortBy = settings?.sortBy || 'performance'
    let chartData = data.filter(d => Math.abs(d.sales) >= 1 || Math.abs(d.discount) >= 1)
    
    if (sortBy === 'name') {
        chartData.sort((a, b) => (a.storeName || '').localeCompare(b.storeName || ''))
    } else if (sortBy === 'number') {
        chartData.sort((a, b) => {
            const numA = parseInt(a.storeNumber) || 0;
            const numB = parseInt(b.storeNumber) || 0;
            return numA - numB;
        })
    } else { // performance (net sales)
        chartData.sort((a, b) => b.sales - a.sales)
    }

    const labels = chartData.map(d => {
        const name = d.storeName || d.storeId
        return d.storeNumber ? `${name} (${d.storeNumber})` : name
    })
    const netSalesData = chartData.map(d => d.sales)
    const discountData = chartData.map(d => d.discount)

    // Determine highlight colors
    // Default chart color rules from useChartColors: colors.primary, colors.primarySubtle
    // Highlight logic: if a specific store is selected, color it bright, others dim.
    // If 'all' or 'group' is selected, color all normally.
    const isSpecificStoreParams = selectedStoreId && selectedStoreId !== 'all' && selectedStoreId !== 'group'

    const isSelected = (id: string) => id.toLowerCase() === selectedStoreId?.toLowerCase()
    
    const basePrimary = colors.primary
    const baseSubtle = colors.primarySubtle
    
    const netSalesBgColors = chartData.map(d => {
        if (!isSpecificStoreParams || isSelected(d.storeId)) return basePrimary;
        return colorMode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
    })
    
    const discountBgColors = chartData.map(d => {
        if (!isSpecificStoreParams || isSelected(d.storeId)) return baseSubtle;
        return colorMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
    })

    const showDiscounts = settings?.showDiscounts !== false

    const datasets: any[] = [
        {
            label: 'Net Sales',
            data: netSalesData,
            backgroundColor: netSalesBgColors,
            borderRadius: 4, 
            stack: 'sales',
        }
    ]

    if (showDiscounts) {
        datasets.push({
            label: 'Discounts',
            data: discountData,
            backgroundColor: discountBgColors,
            borderRadius: 4, 
            stack: 'sales',
        })
    }

    const minVal = Math.min(...netSalesData);
    const xMin = minVal < -100 ? undefined : 0;

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        interaction: { mode: 'index', intersect: false, axis: 'y' },
        plugins: {
            legend: { position: 'bottom' as const, labels: { usePointStyle: true, pointStyle: 'rect' as const, boxWidth: 10, color: textColor, font: { size: 12 } } },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)', titleFont: { size: 12 }, bodyFont: { size: 11 },
                callbacks: { label: (ctx: any) => `${ctx.dataset.label}: $${(ctx.parsed.x ?? 0).toLocaleString()}` }
            }
        },
        scales: {
            x: {
                stacked: true, grid: { color: gridColor }, min: xMin,
                ticks: { color: textColor, font: { size: 11 }, callback: (v: any) => `$${Number(v).toLocaleString()}` }
            },
            y: { 
                stacked: true, grid: { display: false }, 
                ticks: { color: textColor, font: { size: 11 }, autoSkip: false } 
            }
        }
    }

    return (
        <div className="chart-panel">
            <h3 className="chart-title" style={{ padding: '16px 16px 0', margin: 0, fontSize: '1rem', fontWeight: 600 }}>Sales by Store</h3>
            <div style={{ flex: 1, minHeight: 0, padding: 16 }}>
                <Bar data={{ labels, datasets }} options={chartOptions} />
            </div>
        </div>
    )
}
